import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Card, Collapse } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { isEmpty, sumBy } from 'lodash';
import {
  calculateAdmissionFeeCommission,
  price,
  showUpSell,
  someExperienceIncludesProductType,
} from 'utils/helpers';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import LoadingSpinner from 'components/loading_spinner/loading_spinner';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import { getAdmissionFeeDetail } from 'redux/selectors/ui/sealink';
import { CancelTrip } from './cancel_trip/cancel_trip';
import { isOnJourneyPage } from './helpers';
import { FerryDetailPropType, FerryViewPropType } from './prop_types';
import Service from './service';

const FerryDetail = ({ detail, title, altIcon }) => {
  const showLabel = detail.products.length > 1;

  return (
    <div className="space-y-2">
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <i
            className={`card-body__double-arrow me-1 fad ${
              altIcon || 'fa-chevron-double-right'
            }`}
          />{' '}
          <h5>{title}</h5>
        </div>
        {detail.showCancelBtn ? (
          <CancelTrip reservationIds={detail.reservationIds} />
        ) : null}
      </div>
      <p>{detail.date}</p>
      <div className="space-y-3">
        {detail.products.map((product, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index}>
            {!!index && <hr />}
            <Service
              startTime={product.departure.departure_time}
              endTime={product.arrival.arrival_time}
              fromRouteName={product.departure.name}
              toRouteName={product.arrival.name}
              label={(showLabel && product.label) || ''}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export const FerryView = ({
  onlyShowInfo = false,
  ferry,
  actions,
  isDeletingReservation = false,
}) => {
  const location = useLocation();
  const [ferryOpen, setFerryOpen] = useState(true);
  const {
    show: showAdmission,
    totalPrice: totalAdmissionPrice,
    priceList: admissionPriceList,
  } = useSelector(getAdmissionFeeDetail);
  const { currentStep = '' } = useSelector(
    (state) => state.ui?.sealink?.metaFlow || {}
  );
  const {
    isActive,
    canEditBooking,
    isFullyPaid,
    isDraft,
    isEditing,
    canEditReturnOnly,
  } = useCurrentBookingState();
  const showSubtotal = ferry.price !== 0;
  const admissionUrl = ecom.craftConfig.bookingPreferences?.admissionFees?.url;
  const createBookingOnDepartureForm =
    ecom.craftConfig.bookingPreferences?.createBookingOnDepartureForm;
  const { enableMultipleVehicles } =
    ecom.craftConfig.features?.vehicleForm || {};
  const currentBooking = useSelector(getCurrentBooking);
  const editPath = isDraft ? `/${currentBooking?.id}/` : '/';

  // Hide price on all booking process pages when createBookingOnDepartureForm is enabled
  const hidePrice =
    createBookingOnDepartureForm && currentStep.startsWith('/bookings/ferry');
  // Hide price of vehicle when feature of multiple vehicles is enabled.
  const hideVehiclePrice =
    enableMultipleVehicles && /^\/bookings(\/\d+)?\/ferry/.test(currentStep);

  // Calculate admission fee commission and add it to the total commission for display
  const admissionFeeCommission = calculateAdmissionFeeCommission(
    totalAdmissionPrice,
    ferry.commissionPercentage
  );
  const totalCommissionPrice =
    (ferry.commissionPrice || 0) + admissionFeeCommission;
  // Subtract admission fee commission from subtotal (base commission already subtracted in selector)
  const adjustedSubtotal = ferry.price - admissionFeeCommission;

  const loading = () => (
    <div className="card-body--no-data card-body d-flex align-items-center justify-content-center">
      <LoadingSpinner text="Deleting reservation..." />
    </div>
  );

  const showEditButton = useMemo(() => {
    const onJourneyPage = isOnJourneyPage({
      pathname: location.pathname,
    });

    // Hide it on the journey page
    if (onJourneyPage) {
      return false;
    }

    // Hide it if the booking is active
    if (isActive) {
      return false;
    }

    // Hide it when editing a return only booking
    if (isEditing && canEditBooking && canEditReturnOnly) {
      return false;
    }

    return true;
  }, [
    location.pathname,
    canEditBooking,
    canEditReturnOnly,
    isEditing,
    isActive,
  ]);

  const renderFerryReservation = () => (
    <Card.Body>
      {!onlyShowInfo && !isFullyPaid && (
        <div className="d-flex justify-content-end space-x-2 mb-2">
          {showEditButton && (
            <button
              type="button"
              className="link-grey text-decoration-underline text-small"
              onClick={() => actions.editFerry(`/bookings${editPath}ferry`)}
            >
              Edit
            </button>
          )}
          {!isActive && !isDraft && (
            <button
              type="button"
              className="link-danger text-decoration-underline text-small"
              onClick={() => actions.removeFerry()}
            >
              Remove
            </button>
          )}
        </div>
      )}

      {ferry.departureFerry && (
        <FerryDetail detail={ferry.departureFerry} title="Departure Ferry" />
      )}
      {ferry.returnFerry && (
        <>
          <hr />
          <FerryDetail
            detail={ferry.returnFerry}
            title="Return Ferry"
            altIcon="fa-chevron-double-left"
          />
        </>
      )}
      {ferry.services &&
        ferry.services.map((service, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index}>
            {!!index && <hr />}
            <p className="text-x-small">{service.date}</p>
            <div className="space-y-3">
              <Service
                startTime={service.departure.departure_time}
                endTime={service.arrival.arrival_time}
                fromRouteName={service.departure.name}
                toRouteName={service.arrival.name}
                label={service.label || ''}
              />
            </div>
          </div>
        ))}
      {!isEmpty(ferry.passengers) && <hr />}
      <div className="space-y-2">
        {ferry.passengers.map(
          ({ name, count, price: priceValue, adjustments = [] }) => {
            const actualPrice = admissionPriceList[name]
              ? priceValue - admissionPriceList[name]
              : priceValue;
            const hasAdjustments = adjustments.length > 0;
            const totalAdjustment = hasAdjustments
              ? sumBy(adjustments, 'amount')
              : 0;
            // totalAdjustment is negative (discounts), so subtracting it
            // gives the pre-discount price to display on the main row
            const displayPrice = hasAdjustments
              ? actualPrice - totalAdjustment
              : actualPrice;

            return (
              <div key={name}>
                <div className="d-flex justify-content-between text-small">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-user-friends card-body__icon" />
                    <span className="card-body__count-name ps-2">
                      {count} {name}
                    </span>
                  </div>
                  {(Boolean(displayPrice) || hasAdjustments) && !hidePrice && (
                    <div className="fw-bold">{price(displayPrice)}</div>
                  )}
                </div>
                {hasAdjustments &&
                  !hidePrice &&
                  adjustments.map((adj, idx) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={idx}
                      className="d-flex justify-content-between text-small ps-4"
                    >
                      <span className="text-muted">{adj.description}</span>
                      <span className="text-success fw-bold">
                        {price(adj.amount)}
                      </span>
                    </div>
                  ))}
              </div>
            );
          }
        )}

        {ferry.vehicles.map((vehicle, vehicleIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={vehicleIndex}>
            <div className="d-flex justify-content-between text-small mb-1">
              <div>
                <div className="d-flex align-items-center">
                  <i className={`${vehicle.icon} card-body__icon fa-fw me-2`} />
                  <div className="fw-bold">{vehicle.name}</div>
                </div>
              </div>
              {!hidePrice && !hideVehiclePrice && (
                <div className="fw-bold">{vehicle.price}</div>
              )}
            </div>
            <div className="ms-4 space-y-1 text-small">
              {vehicle.trailer && <div>{vehicle.trailer}</div>}
              {vehicle.attachments && (
                <>
                  {vehicle.attachments.map(({ name, count }) => (
                    <div key={name}>
                      {name}
                      {count && (
                        <span className="text-grey ms-1">x{count}</span>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        ))}

        {Boolean(ferry.optionalExtras?.length) && (
          <div>
            <div className="d-flex justify-content-between text-small mb-1">
              <div>
                <div className="d-flex align-items-center">
                  <i className="fa-solid fa-circle-plus card-body__icon fa-fw me-2" />
                  <div className="fw-bold">Optional Extras</div>
                </div>
              </div>
              {!hidePrice && (
                <div className="fw-bold">
                  {price(sumBy(ferry.optionalExtras, (item) => item.price))}
                </div>
              )}
            </div>
            <div className="ms-4 space-y-1 text-small">
              {ferry.optionalExtras.map(({ name, count }) => (
                <div key={name}>
                  {name} <span className="text-grey ms-1">x{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {!!totalCommissionPrice && (
          <div className="d-flex justify-content-between text-small">
            <div>
              <div className="d-flex align-items-center">
                <i className="fas fa-usd-circle card-body__icon me-2" />
                <div className="ps-1">Commission</div>
              </div>
            </div>
            {!hidePrice && (
              <div className="fw-bold">{price(totalCommissionPrice)}</div>
            )}
          </div>
        )}
        {ferry.customerFacingAdjustments?.map((group) => (
          <div
            className="d-flex justify-content-between text-small"
            key={group.category_id}
          >
            <div>
              <div className="d-flex align-items-center">
                <i className="fas fa-circle-info card-body__icon me-2" />
                <div className="ps-1">{group.display_name || 'Surcharge'}</div>
              </div>
            </div>
            {!hidePrice && <div className="fw-bold">{price(group.total)}</div>}
          </div>
        ))}
      </div>
      {showAdmission && (
        <>
          <hr />
          <div className="d-flex justify-content-between">
            <div>
              <div className="d-flex">
                <i className="fa-solid fa-ticket card-body__icon me-2" />
                <div className="lh-1">
                  Government admission fees
                  {admissionUrl && (
                    <div className="mt-2 text-small">
                      <a href={admissionUrl} target="_blank" rel="noreferrer">
                        About this fee
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {Boolean(totalAdmissionPrice) && !hidePrice && (
              <div className="fw-bold lh-1">{price(totalAdmissionPrice)}</div>
            )}
          </div>
          <div className="ms-4">
            {ferry.passengers.map((passenger) => (
              <div key={passenger.name}>
                {passenger.name}
                <span className="text-grey ms-1">x{passenger.count}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card.Body>
  );

  // Auto expand section on receipt page
  useEffect(() => {
    setFerryOpen(ferryOpen || isFullyPaid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullyPaid]);

  if (onlyShowInfo) {
    return renderFerryReservation();
  }

  return (
    <>
      <Card border="primary">
        <Card.Header bg="primary" onClick={() => setFerryOpen(!ferryOpen)}>
          <div data-testid="ferry-header">Ferry</div>
          <div>
            {showSubtotal && !hidePrice && price(adjustedSubtotal)}
            <i
              className={`fas ms-3 ${
                ferryOpen ? 'fa-chevron-up' : 'fa-chevron-down'
              }`}
            />
          </div>
        </Card.Header>
        <Collapse in={ferryOpen}>
          <div>
            {isDeletingReservation ? loading() : renderFerryReservation()}
            {showSubtotal && !hidePrice && (
              <Card.Footer>
                <div className="fw-bold">Subtotal</div>
                <div className="fw-bold">{price(adjustedSubtotal)}</div>
              </Card.Footer>
            )}
          </div>
        </Collapse>
      </Card>
    </>
  );
};

FerryView.propTypes = FerryViewPropType;
FerryDetail.propTypes = FerryDetailPropType;

const FerryViewNoDateCardContent = forwardRef(() => {
  const currentBooking = useSelector(getCurrentBooking);
  const experienceIncludesFerry = someExperienceIncludesProductType(
    'ferry',
    currentBooking
  );

  if (experienceIncludesFerry) {
    return (
      <Card.Body className="card-body--no-data text-grey">
        Your experience includes a ferry trip
      </Card.Body>
    );
  }

  if (showUpSell('ferry', currentBooking)) {
    return (
      <Card.Body className="card-body--no-data text-grey">
        Book your ferry trip
        <div>
          <Link to="/bookings/ferry" className="btn btn-outline-primary">
            View ferry
          </Link>
        </div>
      </Card.Body>
    );
  }

  return <Card.Body className="card-body--no-data">No ferry added</Card.Body>;
});

export const FerryViewNoData = () => {
  const [ferryOpen, setFerryOpen] = useState(true);

  return (
    <Card>
      <Card.Header onClick={() => setFerryOpen(!ferryOpen)}>
        <div data-testid="ferry-header">Ferry</div>
        <div>
          <i
            className={`fas ${ferryOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}
          />
        </div>
      </Card.Header>
      <Collapse in={ferryOpen}>
        <div>
          <FerryViewNoDateCardContent />
        </div>
      </Collapse>
    </Card>
  );
};
